import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ConteudoAjuda } from './tipos';
import { carregarAjuda, temAjudaPara } from './conteudo';

/**
 * O usuário está digitando num campo? Então `?` é pontuação, não atalho.
 *
 * Sem esta guarda, escrever "?" no filtro DESCRIÇÃO abriria a ajuda no meio da digitação.
 */
const digitandoEmCampo = (alvo: EventTarget | null): boolean => {
  const elemento = alvo as HTMLElement | null;
  if (!elemento || !elemento.tagName) return false;

  const tag = elemento.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || elemento.isContentEditable;
};

/**
 * Estado da ajuda da tela atual, mais o atalho de teclado.
 *
 * Vive dentro do HeaderComponent, que 38 telas já usam - por isso não há Provider, nada no
 * App.tsx e nenhuma alteração nas telas.
 *
 * ---------------------------------------------------------------------------------------
 * O BOTÃO APARECE EM TODA TELA, TENHA TEXTO OU NÃO
 *
 * [AJUSTE 21/08/2026] A primeira versão só mostrava o botão onde havia texto. Parecia certo -
 * um "?" que abre "sem ajuda aqui" em 84 de 85 rotas soa vazio - mas na prática o usuário
 * procurou o botão na tela de Contas a **Pagar**, não achou, e teve de perguntar onde estava.
 * Com a cobertura baixa, **ausência é indistinguível de defeito**.
 *
 * Agora o botão está sempre lá e, onde o texto ainda não existe, o painel diz isso com
 * franqueza e lista as telas que já têm. Assim a pessoa aprende que a ajuda existe e onde
 * procurar, em vez de concluir que não existe.
 *
 * ---------------------------------------------------------------------------------------
 * O ATALHO É `?`, E NÃO F1
 *
 * F1 não funciona em navegador: o Chrome captura a tecla na própria janela e abre a ajuda
 * DELE - o `keydown` nunca chega na página, então o preventDefault nem roda. Confirmado em
 * homologação em 21/08. F1 é convenção de aplicativo DESKTOP; na web a convenção é `?`, que é
 * o que GitHub, Gmail, Slack e Jira usam. O F1 continua escutado porque não custa nada e
 * funciona em navegador que deixe passar, mas a legenda anuncia o `?`.
 *
 * Nada disso vale para descoberta: **ninguém acha atalho em sistema web**. Quem faz a ajuda
 * existir é o botão visível.
 *
 * ---------------------------------------------------------------------------------------
 * POR QUE O CONTROLE DE CARGA É UM REF
 *
 * A primeira versão travava em "Carregando…" na primeira abertura, porque `conteudo` estava
 * nas dependências do efeito que PREENCHE `conteudo`: quando o texto chegava, o efeito
 * re-executava e o cleanup suprimia o próprio `setCarregando(false)`. O efeito cancelava a
 * própria conclusão.
 *
 * A lição é geral: **estado que o efeito escreve não entra nas dependências dele.**
 */
export const useAjuda = () => {
  const { pathname } = useLocation();
  const [aberto, setAberto] = useState(false);
  const [conteudo, setConteudo] = useState<ConteudoAjuda | null>(null);
  const [carregando, setCarregando] = useState(false);

  /** Rota cujo texto já foi buscado. Ref de propósito - ver o comentário acima. */
  const rotaCarregada = useRef<string | null>(null);

  /** Esta tela ainda não tem ajuda escrita. Não é erro - é o estado normal hoje. */
  const semAjuda = !temAjudaPara(pathname);

  const abrir = useCallback(() => setAberto(true), []);
  const fechar = useCallback(() => setAberto(false), []);

  // Trocou de tela: fecha o painel e esquece o texto anterior. Sem isto, sair de uma tela com
  // o painel aberto o deixaria aberto na próxima, mostrando o texto da tela errada.
  useEffect(() => {
    setAberto(false);
    setConteudo(null);
    setCarregando(false);
    rotaCarregada.current = null;
  }, [pathname]);

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      const ehInterrogacao = evento.key === '?' && !digitandoEmCampo(evento.target);
      const ehF1 = evento.key === 'F1';

      if (!ehInterrogacao && !ehF1) return;

      evento.preventDefault();
      setAberto((estava) => !estava);
    };

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, []);

  // Busca o texto na primeira abertura de cada rota que tenha texto.
  useEffect(() => {
    if (!aberto || semAjuda) return;
    if (rotaCarregada.current === pathname) return;

    const rota = pathname;
    rotaCarregada.current = rota;
    setCarregando(true);

    carregarAjuda(rota)
      .then((texto) => setConteudo(texto))
      .catch(() => {
        // Libera a rota para nova tentativa: o painel diz "tente novamente", e reabrir tem
        // de realmente tentar de novo.
        setConteudo(null);
        rotaCarregada.current = null;
      })
      .finally(() => setCarregando(false));
  }, [aberto, semAjuda, pathname]);

  return { aberto, abrir, fechar, conteudo, carregando, semAjuda };
};
